import { ArrowLeft, RefreshCw } from "lucide-react";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Badge, Button, Loading } from "@/components";
import { getPanelBrandSlug } from "@/config/config";
import { useI18n } from "@/context/i18n";
import { useHasAnyPermission } from "@/context/permissions";

import { CreditProductForm } from "./components/products-form/credit-product-form";
import { InsuranceAutoForm } from "./components/products-form/insurance-auto-form";
import { InsuranceFuneralForm } from "./components/products-form/insurance-funeral-form";
import { InsuranceGroupLifeForm } from "./components/products-form/insurance-group-life-form";
import { InsurancePersonalAccidentForm } from "./components/products-form/insurance-personal-accident-form";
import { InsuranceWarrantyForm } from "./components/products-form/insurance-warranty-form";
import { isInfinityAssistanceProduct } from "./products.assistance-variant";
import { TProductFormData } from "./products.schema";
import { EProductType } from "./products.type";
import { useProductEdit } from "./products-edit.hooks";
import { isInfinitySyncedProduct } from "./products-sync-origin";

export const ProductsEditPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get("mode") === "view";
  const hasViewPermission = useHasAnyPermission(["products:view"]);
  const hasEditPermission = useHasAnyPermission(["products:edit"]);

  const { product, isLoading, isError, updateProduct, productForm } =
    useProductEdit(id!);

  React.useEffect(() => {
    if (!isLoading && !hasViewPermission) {
      navigate("/products");
    }
  }, [isLoading, hasViewPermission, navigate]);

  React.useEffect(() => {
    if (!isLoading && isViewMode && !hasViewPermission) {
      navigate("/products");
    }
  }, [isLoading, isViewMode, hasViewPermission, navigate]);

  React.useEffect(() => {
    if (!isLoading && !isViewMode && !hasEditPermission) {
      navigate(`/products/${id}?mode=view`, { replace: true });
    }
  }, [id, isLoading, isViewMode, hasEditPermission, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading />
      </div>
    );
  }

  if (!hasViewPermission) {
    return null;
  }

  if (isError || !product) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <p className="text-muted-foreground">Produto não encontrado</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/products")}
        >
          {t("generic.goBack")}
        </Button>
      </div>
    );
  }

  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case EProductType.INSURANCE_AP:
        return "Seguro AP";
      case EProductType.INSURANCE_VG:
        return "Seguro VG";
      case EProductType.INSURANCE_ASSISTANCE:
        return isInfinityAssistanceProduct(product)
          ? "Assistências - Infinity"
          : "Assistência - Soulhub";
      case EProductType.CREDIT:
        return "Crédito";
      case EProductType.AUTO:
        return "Auto";
      case EProductType.MOTO:
        return "Moto";
      case EProductType.WARRANTY_INSURANCE:
        return t(`product.type.${EProductType.WARRANTY_INSURANCE}`);
      case EProductType.THEFT_INSURANCE:
        return t(`product.type.${EProductType.THEFT_INSURANCE}`);
      default:
        return type;
    }
  };

  const handleSubmit = (data: TProductFormData) => {
    if (isViewMode) {
      return;
    }
    updateProduct(data);
  };

  const renderProductForm = () => {
    const commonProps = {
      open: true,
      onOpenChange: () => {},
      form: productForm,
      onSubmit: handleSubmit,
      isEditMode: !isViewMode,
      productId: id,
    };

    switch (product?.type) {
      case EProductType.INSURANCE_AP:
        return <InsurancePersonalAccidentForm {...commonProps} />;

      case EProductType.INSURANCE_VG:
        return <InsuranceGroupLifeForm {...commonProps} />;

      case EProductType.INSURANCE_ASSISTANCE:
        return isInfinityAssistanceProduct(product) ? (
          <InsuranceFuneralForm {...commonProps} />
        ) : getPanelBrandSlug() === "soulhub" ? (
          <InsuranceWarrantyForm
            {...commonProps}
            assistanceSoulhubMode
            productKind="warranty"
          />
        ) : (
          <InsuranceFuneralForm {...commonProps} />
        );

      case EProductType.AUTO:
      case EProductType.MOTO:
        return <InsuranceAutoForm {...commonProps} />;

      case EProductType.CREDIT:
        return <CreditProductForm {...commonProps} />;

      case EProductType.WARRANTY_INSURANCE:
        return <InsuranceWarrantyForm {...commonProps} productKind="warranty" />;

      case EProductType.THEFT_INSURANCE:
        return <InsuranceWarrantyForm {...commonProps} productKind="theft" />;

      default:
        return <InsurancePersonalAccidentForm {...commonProps} />;
    }
  };

  return (
    <div className="flex min-h-0 w-full flex-col space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/products")}
              size="sm"
              className="p-3 hover:bg-muted/50 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {isViewMode
                ? t("products.page.viewTitle")
                : t("products.page.editTitle")}
            </h1>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                {isViewMode
                  ? t("products.page.viewingLabel")
                  : t("products.page.editingLabel")}
              </span>
              <span className="text-sm font-semibold text-primary">
                {product.name}
              </span>
            </div>
            {product.type && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Tipo:
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {getProductTypeLabel(product.type)}
                </span>
              </div>
            )}
            {isInfinitySyncedProduct(product) && (
              <div className="mt-3 rounded-lg border border-primary/20 bg-muted/30 p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className="gap-1 border-primary/40 text-primary"
                  >
                    <RefreshCw className="h-3 w-3" />
                    {t("products.sync.badge")}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("products.sync.sourceOrganizationsHint")}
                </p>
                {(product.infinity_sync_source_organizations?.length ?? 0) >
                  0 && (
                  <div className="flex flex-wrap gap-1">
                    {product.infinity_sync_source_organizations!.map(
                      (label) => (
                        <span
                          key={label}
                          className="inline-flex rounded-md bg-background px-2 py-0.5 text-xs border"
                        >
                          {label}
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={isViewMode ? "product-details-view-mode" : undefined}>
        {renderProductForm()}
      </div>

      {isViewMode && hasEditPermission ? (
        <div className="flex justify-end px-2">
          <Button type="button" onClick={() => navigate(`/products/${id}`)}>
            {t("products.actions.edit")}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
